/**
 * Sharing a link from the donor web (GH-864e): the OS share sheet where the browser has one, the
 * clipboard where it does not, and an honest "couldn't" where neither works.
 *
 * Nothing here decides *which* link — that is the server's (`GET /v1/donor/share-link`) for a
 * member and the page's own address for everyone else — and nothing here talks to the server.
 * The platform never messages anybody: the donor shares, through their own sheet.
 */

export type ShareTarget = { readonly organizationId: string } | { readonly eventId: string };

export type ShareOutcome = "shared" | "copied" | "cancelled" | "failed";

/** The page's own public address — what a non-member shares, carrying no credit. */
export function plainShareUrl(
  target: ShareTarget,
  origin: string = window.location.origin,
): string {
  return "eventId" in target
    ? `${origin}/e/${encodeURIComponent(target.eventId)}`
    : `${origin}/o/${encodeURIComponent(target.organizationId)}`;
}

const isAbort = (caught: unknown): boolean =>
  caught instanceof DOMException && caught.name === "AbortError";

async function copy(url: string): Promise<ShareOutcome> {
  try {
    if (!navigator.clipboard) return "failed";
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    return "failed";
  }
}

/**
 * Open the share sheet, falling back to copying the link.
 *
 * A donor who closes the sheet has made a choice (`cancelled`), not hit an error, so that is never
 * reported as one. Any other refusal — a browser that will not share outside a fresh tap, a page
 * without permission — falls through to the clipboard.
 */
export async function shareOrCopy(share: {
  readonly url: string;
  readonly text?: string | undefined;
  readonly title?: string | undefined;
}): Promise<ShareOutcome> {
  if (typeof navigator.share === "function") {
    // The server's messages end with the link; the sheet attaches `url` itself, so it is taken out
    // of the text rather than arriving twice.
    const text = share.text?.includes(share.url)
      ? share.text.replace(share.url, "").replace(/[\s:]+$/, "")
      : share.text;
    try {
      await navigator.share({
        url: share.url,
        ...(text ? { text } : {}),
        ...(share.title ? { title: share.title } : {}),
      });
      return "shared";
    } catch (caught: unknown) {
      if (isAbort(caught)) return "cancelled";
    }
  }
  return copy(share.url);
}

/** "1,214" — membership numbers arrive as integers and are grouped the way iOS groups them. */
export const grouped = (value: number): string => value.toLocaleString("en-US");
